import { Close } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { Fragment, useEffect, useState } from "react";
import { PolarisObject, User } from "../../../../app/backendTypes";
import {
  usePendingPolarisGroupAccessTask,
  usePolarisGroupMembership,
  usePolarisGroups,
} from "../../../../app/hooks";
import Error from "../../Error";
import Loading from "../../Loading";
import UserSpan from "../../users/UserSpan";
import PendingAction from "../PendingAction";
import Morselbar from "../../Morselbar";
import PolarisGroupPicker from "./PolarisGroupPicker";
import PolarisGroupAccessDialog from "./PolarisGroupAccessDialog";

export default function PolarisGroupsDialog(props: {
  open: boolean;
  setOpen: (val: boolean) => void;
  user: User;
}) {
  const { open, setOpen, user } = props;

  const { groups, loading, error } = usePolarisGroups(!open);

  const [errorOpen, setErrorOpen] = useState(false);

  useEffect(() => {
    if (error) setErrorOpen(true);
  }, [error]);

  const [group, setGroup] = useState<PolarisObject | null>(null);

  useEffect(() => {
    if (group !== null) {
      const newGroup = groups.find((p) => p.id === group.id);
      if (newGroup !== undefined) {
        if (JSON.stringify(newGroup) !== JSON.stringify(group))
          setGroup(newGroup);
      } else {
        setGroup(null);
      }
    }
  }, [group, groups]);

  return (
    <>
      <Dialog open={open} fullWidth>
        <DialogTitle>
          Polaris Groups
          <IconButton
            sx={{ position: "absolute", right: 8, top: 8 }}
            onClick={() => {
              setGroup(null);
              setOpen(false);
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <DialogContentText>Select a group to manage access</DialogContentText>
          <br />
          <PolarisGroupPicker
            groups={groups}
            loading={loading}
            error={error}
            onChange={(val) => setGroup(val)}
            initialValue={group}
          />
          <br />
          <br />
          {group !== null && <GroupAccess user={user} group={group} />}
        </DialogContent>
      </Dialog>

      <Morselbar
        open={errorOpen}
        setOpen={setErrorOpen}
        content="Failed to load groups"
        error={error}
      />
    </>
  );
}

function GroupAccess(props: { user: User; group: PolarisObject }) {
  const { user, group } = props;

  const { membership, loading, error, reload } = usePolarisGroupMembership(
    user.employee.id,
    group.id
  );

  const {
    task,
    loading: taskLoading,
    error: taskError,
    reload: taskReload,
  } = usePendingPolarisGroupAccessTask(user.employee.id, group.id);

  useEffect(() => {
    if (
      task !== null &&
      (task.status.kind === "Done" || task.status.kind === "Cancelled")
    ) {
      reload();
      taskReload();
    }
  }, [reload, task, taskReload]);

  let element = <></>;
  const actions = [];

  if (taskLoading) {
    actions.push(<Loading />);
  } else if (taskError) {
    actions.push(<Error error={taskError} />);
  } else if (task !== null) {
    actions.push(
      <PendingAction
        task={task}
        callback={taskReload}
        onCompleted={taskReload}
      />
    );
  }

  if (loading) element = <Loading />;
  else if (error) element = <Error error={error} />;
  else if (membership !== null) {
    const verb = membership === true ? "is" : "is not";

    element = (
      <Typography>
        <UserSpan user={user} /> {verb} assigned to{" "}
        <Typography component="span" color="secondary">
          {group.attributes.groupname}
        </Typography>
      </Typography>
    );
  }

  if (task === null && membership === false)
    actions.push(
      <RequestGroupAccess
        user={user}
        group={group}
        callback={() => {
          reload();
          taskReload();
        }}
      />
    );

  return (
    <>
      {element}
      <br />
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        {actions.map((a, i) => (
          <Fragment key={i}>{a}</Fragment>
        ))}
      </Box>
    </>
  );
}

function RequestGroupAccess(props: {
  user: User;
  group: PolarisObject;
  callback: () => void;
}) {
  const { user, group, callback } = props;

  const [open, setOpen] = useState(false);

  return (
    <>
      <Button onClick={() => setOpen(true)} color="secondary">
        Request Access
      </Button>

      <PolarisGroupAccessDialog
        open={open}
        setOpen={setOpen}
        user={user}
        group={group}
        callback={callback}
      />
    </>
  );
}
